import {
  CommonModule,
} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
} from '@angular/core';
import type {
  Traveler,
} from '@dapi/sdk';
import {
  O3rComponent,
} from '@o3r/core';
import {
  RequestParametersService,
} from '@o3r/dynamic-content';
import {
  AlternativeOffersAvailabilitySkeletonPresComponent,
  RefundMethodSkeletonPresComponent,
  RefundPreviewSkeletonPresComponent,
} from '@refx/servicing-components';
import {
  RequestParams,
} from '@refx/shared-common';
import {
  BasicLayoutModule,
  BreadcrumbSkeletonPresComponent,
  CalendarSkeletonPresComponent,
  CollapsibleInformationPanelSkeletonPresComponent,
  IdentificationSkeletonPresComponent,
  ItineraryCardSkeletonPresComponent,
  MessagesPanelSkeletonPresComponent,
  PageTitleSkeletonPresComponent,
  PassengerRecapSkeletonPresComponent,
} from '@refx/shared-components';
import {
  NgxSkeletonLoaderModule,
} from 'ngx-skeleton-loader';
import {
  AppRoutes,
  CombinedRefundRoute,
  CombinedSelfReaccRoute,
  skeletalRoutes,
} from '../../../configuration/routes';
import {
  ServicingRequestParams,
} from '../../../types/query-params.enum';

@O3rComponent({
  componentType: 'Component'
})
@Component({
  selector: 'refx-app-skeleton-pres',
  templateUrl: './app-skeleton.template.html',
  styleUrls: ['./app-skeleton.style.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    BasicLayoutModule,
    CalendarSkeletonPresComponent,
    BreadcrumbSkeletonPresComponent,
    CollapsibleInformationPanelSkeletonPresComponent,
    ItineraryCardSkeletonPresComponent,
    IdentificationSkeletonPresComponent,
    MessagesPanelSkeletonPresComponent,
    NgxSkeletonLoaderModule,
    PassengerRecapSkeletonPresComponent,
    RefundMethodSkeletonPresComponent,
    RefundPreviewSkeletonPresComponent,
    AlternativeOffersAvailabilitySkeletonPresComponent,
    PageTitleSkeletonPresComponent
  ]
})
export class AppSkeletonPresComponent {
  /**
   * The route for which the skeleton must be displayed
   */
  public skeletonRoute: CombinedSelfReaccRoute | CombinedRefundRoute | AppRoutes = skeletalRoutes[0];

  /**
   * The list of traveler types in the selected data type
   */
  public travelers: string[] = ['ADT'];

  /**
   * The number of bounds in the Order data
   */
  public nbBounds = 1;

  constructor(
    protected requestParametersService: RequestParametersService
  ) {
    this.determineSkeletonPage();
    this.constructTemplateData();
  }

  /**
   * Constructs the template data for the skeleton from order
   * It makes use of the session storage to fetch the data instead
   * of the store because the store is not populated yet and also
   * to avoid bloating the main bundle size with references to the
   * Order model and their dependencies since this skeleton
   * is part of the main bundle
   */
  protected constructTemplateData() {
    try {
      const rawOrder = sessionStorage.getItem('order');
      if (rawOrder) {
        const order = JSON.parse(rawOrder);
        const currentOrder = order?.entities?.[order?.ids?.[0]];
        if (currentOrder) {
          this.travelers = currentOrder.travelers?.map((traveler: Traveler) => traveler.passengerTypeCode || 'ADT') || ['ADT'];
          this.nbBounds = currentOrder.air.bounds.length || 1;
        }
      }
    } catch {}
  }

  /**
   * Determines if there are deeplink parameters
   */
  protected areDeeplinkParametersPresent() {
    const recLoc = this.requestParametersService.getParameter(ServicingRequestParams.recLoc);
    const lastName = this.requestParametersService.getParameter(ServicingRequestParams.lastName);
    const encrypt = this.requestParametersService.getPostParameter(RequestParams.encrypt);
    return (recLoc && lastName) || encrypt;
  }

  /**
   * Determines the page type to display the skeleton for
   */
  protected determineSkeletonPage() {
    const route = window.location.pathname;
    this.skeletonRoute = skeletalRoutes.find((skeletalRoute) => route.includes(skeletalRoute)) || skeletalRoutes[0];
    this.skeletonRoute = this.skeletonRoute === skeletalRoutes[0] ? this.areDeeplinkParametersPresent() ? skeletalRoutes[1] : skeletalRoutes[0] : this.skeletonRoute;
  }
}
